import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Archive, RotateCcw } from "lucide-react";
import { API, api, errMsg, money, specValue } from "@/lib/api";
import { useSpecSchema } from "@/context/SpecSchemaContext";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import { StatusChip, AvailabilityChip } from "@/components/StatusChip";
import { Btn, Card, Empty, Field, Input, Loading, Modal, Pill, Select, StatCard, Table, Textarea, STATUS_TONE, labelCls } from "@/components/Ui";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminSupport from "@/pages/AdminSupport";
import AdminSpecCategories from "@/pages/AdminSpecCategories";
import AdminPayments from "@/pages/AdminPayments";
import { CountrySelect } from "@/components/CountrySelect";

const NAV = [
  { to: "/admin", label: "Dashboard", end: true, testId: "admin-nav-dashboard" },
  { to: "/admin?tab=manufacturers", label: "Manufacturers", testId: "admin-nav-manufacturers" },
];

const SUB_TONE = { pending: "warn", approved: "ok", rejected: "bad", changes_requested: "warn" };

const TABS = [
  ["overview", "Dashboard"],
  ["manufacturers", "Manufacturers"],
  ["products", "Products"],
  ["equivalents", "Equivalents"],
  ["categories", "Categories"],
  ["buyers", "Buyers"],
  ["suppliers", "Suppliers"],
  ["submissions", "Supplier submissions"],
  ["rfqs", "RFQs"],
  ["quotes", "Quotations"],
  ["awards", "Awards & Managed"],
  ["reports", "Reports & Analytics"],
  ["support", "Support inbox"],
  ["payments", "Payments"],
  ["spec-fields", "Categories & specs"],
  ["matching", "Matching rules"],
  ["audit", "Audit log"],
];

export default function AdminPortal() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const schema = useSpecSchema();

  const [d, setD] = useState({ stats: null, manufacturers: [], products: [], equivalents: [],
    categories: [], buyers: [], suppliers: [], rfqs: [], quotes: [], awards: [], rules: null, logs: [],
    submissions: [], batches: [] });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [draft, setDraft] = useState({});
  const specInputs = schema.inputsFor(draft.category);

  const [importing, setImporting] = useState("");
  const [selected, setSelected] = useState([]);
  const [pickedProducts, setPickedProducts] = useState([]);
  const [pickedEquivs, setPickedEquivs] = useState([]);
  const importFileRef = useRef(null);

  const toggleIn = (setter) => (id) =>
    setter((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const togglePickedProduct = toggleIn(setPickedProducts);
  const togglePickedEquiv = toggleIn(setPickedEquivs);

  const bulkProducts = (action) => {
    const label = { archive: "archived", restore: "restored", delete: "deleted" }[action];
    if (action === "delete" &&
        !window.confirm(`Delete ${pickedProducts.length} product(s)? Products already used in an RFQ or quote will be archived instead.`)) return;
    return act(async () => {
      const { data } = await api.post("/admin/products/bulk-action",
        { product_ids: pickedProducts, action });
      setPickedProducts([]);
      if (data.archived_instead) toast.info(`${data.archived_instead} product(s) were archived instead — they are referenced by an RFQ or quote`);
    }, action === "delete"
      ? `Deleted ${pickedProducts.length} product(s)`
      : `${pickedProducts.length} product(s) ${label}`);
  };

  const bulkEditProducts = () => act(async () => {
    const changes = {};
    ["manufacturer_id", "category", "subcategory", "country_of_manufacture", "brand",
     "currency", "list_price", "spec_key", "spec_value"].forEach((k) => {
      if (draft[k] !== "" && draft[k] != null) changes[k] = draft[k];
    });
    await api.post("/admin/products/bulk-update", { product_ids: draft.product_ids, changes });
    setPickedProducts([]);
  }, "Products updated");

  const bulkEquivs = (action) => {
    if (action === "delete" && !window.confirm(`Remove ${pickedEquivs.length} equivalence link(s)?`)) return;
    return act(async () => {
      await api.post("/admin/equivalents/bulk", { equivalent_ids: pickedEquivs, action });
      setPickedEquivs([]);
    }, action === "delete" ? "Links removed" : "Notes updated");
  };

  const load = useCallback(async () => {
    try {
      const [stats, manufacturers, products, equivalents, categories, buyers, suppliers, rfqs, quotes, awards, rules, logs, submissions, batches] =
        await Promise.all([
          api.get("/admin/stats"), api.get("/manufacturers"),
          api.get("/products", { params: { limit: 500, include_archived: true } }),
          api.get("/admin/equivalents"), api.get("/admin/categories"),
          api.get("/admin/companies", { params: { type: "buyer" } }),
          api.get("/admin/companies", { params: { type: "supplier" } }),
          api.get("/admin/rfqs"), api.get("/admin/quotes"), api.get("/admin/awards"),
          api.get("/admin/matching-rules"), api.get("/admin/audit-logs"),
          api.get("/admin/submissions"), api.get("/admin/submission-batches"),
        ]);
      setD({ stats: stats.data, manufacturers: manufacturers.data, products: products.data.results,
        equivalents: equivalents.data, categories: categories.data, buyers: buyers.data,
        suppliers: suppliers.data, rfqs: rfqs.data, quotes: quotes.data, awards: awards.data,
        rules: rules.data, logs: logs.data, submissions: submissions.data, batches: batches.data });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pendingBatches = useMemo(() => (d?.batches || []).filter((b) => b.pending > 0), [d]);
  const companies = useMemo(
    () => (tab === "buyers" ? d?.buyers : d?.suppliers) || [], [tab, d]);

  const act = async (fn, msg) => {
    try {
      await fn();
      await load();
      setModal(null);
      toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveMfr = () => act(async () => {
    const body = { name: draft.name, country: draft.country, website: draft.website, notes: draft.notes };
    if (draft.id) await api.put(`/admin/manufacturers/${draft.id}`, body);
    else await api.post("/admin/manufacturers", body);
  }, "Manufacturer saved");

  const saveProduct = () => act(async () => {
    const spec = {};
    specInputs.forEach(([k, , type]) => {
      const v = draft.spec?.[k];
      if (v !== "" && v != null) spec[k] = type === "number" ? Number(v) : v;
    });
    const body = { part_number: draft.part_number, name: draft.name,
      manufacturer_id: draft.manufacturer_id, brand: draft.brand || null,
      product_family: draft.product_family || null, category: draft.category || null,
      subcategory: draft.subcategory || null, description: draft.description || null,
      country_of_manufacture: draft.country_of_manufacture || null,
      technical_notes: draft.technical_notes || null, image_url: draft.image_url || null,
      list_price: draft.list_price === "" || draft.list_price == null ? null : Number(draft.list_price),
      currency: draft.currency || "AED",
      is_archived: !!draft.is_archived, spec };
    if (draft.id) await api.put(`/admin/products/${draft.id}`, body);
    else await api.post("/admin/products", body);
  }, "Product saved");

  const importProducts = () => act(async () => {
    let rows;
    try {
      rows = JSON.parse(draft.json || "[]");
    } catch {
      throw new Error("Invalid JSON");
    }
    const { data } = await api.post("/admin/products/import", { rows });
    if (data.errors?.length) toast.error(`${data.errors.length} row(s) failed`);
  }, "Import completed");

  const runFileImport = async (dry) => {
    if (!draft.file) return;
    setImporting(dry ? "dry" : "run");
    try {
      const fd = new FormData();
      fd.append("file", draft.file);
      const { data } = await api.post(`/admin/products/import-file?dry_run=${dry}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDraft((d) => ({ ...d, result: data }));
      if (!dry) {
        await load();
        toast.success(`${data.created} created, ${data.updated} updated`);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setImporting("");
    }
  };

  if (loading) return <div className="mx-auto max-w-[1240px] px-5"><Loading label="Loading admin data…" /></div>;

  const s = d.stats || {};

  return (
    <DashboardShell title="ADIT administration" subtitle="Platform control" nav={[]} testId="admin-dashboard">
      <div className="-mt-2">
        <div className="flex flex-wrap gap-2 border-b border-[#DFE6ED] pb-3">
          {TABS.map(([k, l]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => { setTab(k); setPickedProducts([]); setPickedEquivs([]); setSelected([]); }}
              className={`rounded-[9px] px-3.5 py-2 text-sm font-semibold transition-colors ${
                tab === k ? "bg-[#102A43] text-white" : "text-[#667788] hover:bg-[#eef2f7]"}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="mt-6" data-testid="admin-overview">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-stats">
              <StatCard testId="stat-products" label="Active products" value={s.products} sub={`${s.archived_products} archived`} />
              <StatCard testId="stat-manufacturers" label="Manufacturers" value={s.manufacturers} />
              <StatCard testId="stat-crossrefs" label="Equivalent links" value={s.equivalent_links} />
              <StatCard testId="stat-rfqs" label="RFQs" value={s.rfqs} sub={`${s.open_rfqs} open`} />
              <StatCard testId="stat-buyers" label="Buyer companies" value={s.buyers} />
              <StatCard testId="stat-suppliers" label="Supplier companies" value={s.suppliers} />
              <StatCard testId="stat-pending" label="Pending verifications" value={s.pending_verifications} />
              <StatCard testId="stat-pending-submissions" label="Pending submissions" value={s.pending_submissions} />
              <StatCard testId="stat-open-support" label="Open support requests" value={s.open_support_requests} sub="Support inbox" />
              <StatCard testId="stat-quotes" label="Quotations" value={s.quotes} />
              <StatCard testId="stat-awards" label="Awards" value={s.awards} sub={`${s.managed_requests} managed`} />
              <StatCard testId="stat-pro" label="Pro subscriptions" value={s.pro_subscriptions} />
              <StatCard testId="stat-ent" label="Enterprise subscriptions" value={s.enterprise_subscriptions} />
            </div>
            <p className="mt-4 text-xs text-[#8598ac]">
              All figures are live counts from the database. Signed in as {user?.email}.
            </p>
          </div>
        )}

        {tab === "manufacturers" && (
          <div className="mt-6" data-testid="admin-manufacturers">
            <Btn data-testid="add-manufacturer-btn" onClick={() => { setDraft({ name: "", country: "", website: "", notes: "" }); setModal("mfr"); }}>
              <Plus className="h-4 w-4" /> Add manufacturer
            </Btn>
            <Table head={["Name", "Country", "Website", "Actions"]}>
              {d.manufacturers.map((m) => (
                <tr key={m.id} data-testid={`mfr-row-${m.id}`}>
                  <td className="font-semibold text-[#102A43]">{m.name}</td>
                  <td>{m.country || "—"}</td>
                  <td className="break-all text-xs">{m.website || "—"}</td>
                  <td>
                    <div className="flex gap-3">
                      <button data-testid={`edit-mfr-${m.id}`} onClick={() => { setDraft({ ...m }); setModal("mfr"); }} className="text-[#102A43]"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`delete-mfr-${m.id}`} onClick={() => act(() => api.delete(`/admin/manufacturers/${m.id}`), "Manufacturer deleted")} className="text-[#B42318]"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          </div>
        )}

        {tab === "products" && (
          <div className="mt-6" data-testid="admin-products">
            <div className="flex flex-wrap gap-2">
              <Btn data-testid="add-product-btn" onClick={() => { setDraft({ part_number: "", name: "", manufacturer_id: d.manufacturers[0]?.id || "", spec: {} }); setModal("product"); }}>
                <Plus className="h-4 w-4" /> Add product
              </Btn>
              <Btn variant="ghost" data-testid="import-products-btn" onClick={() => { setDraft({ json: "", mode: "file" }); setModal("import"); }}>
                Import products
              </Btn>
            </div>
            {pickedProducts.length > 0 && (
              <div className="sticky top-20 z-20 mt-4 flex flex-wrap items-center gap-2 rounded-[12px] border border-[#DFE6ED] bg-[#F5F7FA] px-4 py-3"
                data-testid="product-bulk-bar">
                <span className="text-sm font-semibold text-[#102A43]" data-testid="product-selected-count">
                  {pickedProducts.length} selected
                </span>
                <Btn variant="ghost" data-testid="bulk-edit-products-btn" className="px-3 py-2 text-xs"
                  onClick={() => { setDraft({ product_ids: pickedProducts, count: pickedProducts.length, manufacturer_id: "", category: "", subcategory: "", country_of_manufacture: "", brand: "", currency: "", list_price: "", spec_key: "", spec_value: "" }); setModal("bulk-product"); }}>
                  Bulk edit
                </Btn>
                <Btn variant="ghost" data-testid="bulk-archive-products-btn" className="px-3 py-2 text-xs"
                  onClick={() => bulkProducts("archive")}>Archive</Btn>
                <Btn variant="ghost" data-testid="bulk-restore-products-btn" className="px-3 py-2 text-xs"
                  onClick={() => bulkProducts("restore")}>Restore</Btn>
                <Btn variant="danger" data-testid="bulk-delete-products-btn" className="px-3 py-2 text-xs"
                  onClick={() => bulkProducts("delete")}>Delete</Btn>
                <button data-testid="clear-picked-products" className="text-xs font-semibold text-[#667788] underline"
                  onClick={() => setPickedProducts([])}>Clear</button>
              </div>
            )}
            <Table head={[
              <input key="all" type="checkbox" data-testid="select-all-products"
                checked={d.products.length > 0 && pickedProducts.length === d.products.length}
                onChange={(e) => setPickedProducts(e.target.checked ? d.products.map((p) => p.id) : [])} />,
              "Part number", "Manufacturer", "Category", "Thread", "Ex / Gas", "IECEx", "State", "Actions"]}>
              {d.products.map((p) => (
                <tr key={p.id} data-testid={`product-row-${p.id}`}>
                  <td>
                    <input type="checkbox" data-testid={`select-product-${p.id}`}
                      checked={pickedProducts.includes(p.id)} onChange={() => togglePickedProduct(p.id)} />
                  </td>
                  <td className="font-semibold text-[#102A43]">{p.part_number}</td>
                  <td>{p.manufacturer_name}</td>
                  <td className="text-xs">{p.subcategory || p.category || "—"}</td>
                  <td>{specValue(p.spec, "thread_size")}</td>
                  <td className="text-xs">{specValue(p.spec, "ex_protection")} / {specValue(p.spec, "gas_group")}</td>
                  <td className="text-xs">{specValue(p.spec, "certificate_reference")}</td>
                  <td><Pill tone={p.is_archived ? "neutral" : "ok"}>{p.is_archived ? "archived" : "active"}</Pill></td>
                  <td>
                    <div className="flex gap-3">
                      <button data-testid={`edit-product-${p.id}`} onClick={() => { setDraft({ ...p, spec: { ...(p.spec || {}) } }); setModal("product"); }} className="text-[#102A43]"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`archive-product-${p.id}`}
                        onClick={() => act(() => api.put(`/admin/products/${p.id}/archive?archived=${!p.is_archived}`), p.is_archived ? "Product restored" : "Product archived")}
                        className="text-[#B7791F]">
                        {p.is_archived ? <RotateCcw className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                      </button>
                      <button data-testid={`delete-product-${p.id}`} onClick={() => act(() => api.delete(`/admin/products/${p.id}`), "Product deleted")} className="text-[#B42318]"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          </div>
        )}

        {tab === "equivalents" && (
          <div className="mt-6" data-testid="admin-equivalents">
            <Btn data-testid="add-equivalent-btn" onClick={() => { setDraft({ product_id: d.products[0]?.id || "", equivalent_product_id: d.products[1]?.id || "", notes: "" }); setModal("equiv"); }}>
              <Plus className="h-4 w-4" /> Link equivalent
            </Btn>
            {pickedEquivs.length > 0 && (
              <div className="sticky top-20 z-20 mt-4 flex flex-wrap items-center gap-2 rounded-[12px] border border-[#DFE6ED] bg-[#F5F7FA] px-4 py-3"
                data-testid="equiv-bulk-bar">
                <span className="text-sm font-semibold text-[#102A43]" data-testid="equiv-selected-count">
                  {pickedEquivs.length} selected
                </span>
                <Btn variant="ghost" data-testid="bulk-notes-equivs-btn" className="px-3 py-2 text-xs"
                  onClick={() => { setDraft({ equivalent_ids: pickedEquivs, count: pickedEquivs.length, notes: "" }); setModal("bulk-equiv"); }}>
                  Set notes
                </Btn>
                <Btn variant="danger" data-testid="bulk-delete-equivs-btn" className="px-3 py-2 text-xs"
                  onClick={() => bulkEquivs("delete")}>Delete</Btn>
                <button data-testid="clear-picked-equivs" className="text-xs font-semibold text-[#667788] underline"
                  onClick={() => setPickedEquivs([])}>Clear</button>
              </div>
            )}
            <Table head={[
              <input key="all" type="checkbox" data-testid="select-all-equivs"
                checked={d.equivalents.length > 0 && pickedEquivs.length === d.equivalents.length}
                onChange={(e) => setPickedEquivs(e.target.checked ? d.equivalents.map((x) => x.id) : [])} />,
              "Product", "Equivalent", "Notes", "Actions"]}>
              {d.equivalents.map((e) => (
                <tr key={e.id} data-testid={`equiv-row-${e.id}`}>
                  <td>
                    <input type="checkbox" data-testid={`select-equiv-${e.id}`}
                      checked={pickedEquivs.includes(e.id)} onChange={() => togglePickedEquiv(e.id)} />
                  </td>
                  <td className="font-semibold text-[#102A43]">{e.product_label}</td>
                  <td>{e.equivalent_label}</td>
                  <td className="text-xs">{e.notes || "—"}</td>
                  <td>
                    <button data-testid={`delete-equiv-${e.id}`} onClick={() => act(() => api.delete(`/admin/equivalents/${e.id}`), "Link removed")} className="text-[#B42318]"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </Table>
          </div>
        )}

        {tab === "categories" && (
          <div className="mt-6" data-testid="admin-categories">
            <Btn data-testid="add-category-btn" onClick={() => { setDraft({ name: "", subcategories: "" }); setModal("category"); }}>
              <Plus className="h-4 w-4" /> Add category
            </Btn>
            {d.categories.length === 0 ? (
              <Empty testId="categories-empty" title="No explicit categories yet"
                sub="Categories are currently derived from product data. Add one to control the public list." />
            ) : (
              <Table head={["Category", "Subcategories", "Actions"]}>
                {d.categories.map((c) => (
                  <tr key={c.id} data-testid={`category-row-${c.id}`}>
                    <td className="font-semibold text-[#102A43]">{c.name}</td>
                    <td className="text-xs">{(c.subcategories || []).join(", ") || "—"}</td>
                    <td>
                      <div className="flex gap-3">
                        <button data-testid={`edit-category-${c.id}`} onClick={() => { setDraft({ name: c.name, subcategories: (c.subcategories || []).join(", ") }); setModal("category"); }} className="text-[#102A43]"><Pencil className="h-4 w-4" /></button>
                        <button data-testid={`delete-category-${c.id}`} onClick={() => act(() => api.delete(`/admin/categories/${c.id}`), "Category deleted")} className="text-[#B42318]"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
        )}

        {(tab === "buyers" || tab === "suppliers") && (
          <div className="mt-6" data-testid={`admin-${tab}`}>
            {companies.length === 0 ? (
              <Empty testId={`${tab}-empty`} title={`No ${tab} registered yet`} />
            ) : (
              <div className="space-y-4">
                {companies.map((c) => (
                  <Card key={c.id} data-testid={`company-card-${c.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-display text-lg font-black tracking-tight text-[#102A43]">{c.name}</p>
                        <p className="text-xs text-[#8598ac]">
                          {[c.supplier_type, c.industry, c.emirate, c.country].filter(Boolean).join(" · ")}
                        </p>
                        <p className="mt-1 text-xs text-[#667788]">
                          Licence: {c.trade_licence || "not provided"} · {c.contact_person || "—"} · {c.email} · {c.phone || "—"}
                        </p>
                        {tab === "suppliers" && (
                          <p className="mt-1 text-xs text-[#667788]">
                            Brands: {(c.brands || []).join(", ") || "—"} · Categories: {(c.categories || []).join(", ") || "—"} · Markets: {(c.markets_served || []).join(", ") || "—"}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Pill tone={STATUS_TONE[c.verification_status] || "warn"} testId={`verification-${c.id}`}>
                          {c.verification_status}
                        </Pill>
                        {tab === "suppliers" && (
                          <>
                            <Pill tone="gold" testId={`plan-${c.id}`}>{c.subscription_plan}</Pill>
                            {c.is_adit_trading && <Pill tone="neutral">ADIT Trading</Pill>}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-[#DFE6ED] pt-4">
                      <div>
                        <label className={labelCls}>Verification</label>
                        <Select data-testid={`set-verification-${c.id}`} value={c.verification_status}
                          onChange={(e) => act(() => api.put(`/admin/companies/${c.id}/verification`, { status: e.target.value }), "Verification updated")}
                          options={["pending", "verified", "rejected"]} />
                      </div>
                      {tab === "suppliers" && (
                        <>
                          <div>
                            <label className={labelCls}>Plan</label>
                            <Select data-testid={`set-plan-${c.id}`} value={c.subscription_plan || "founding"}
                              onChange={(e) => act(() => api.put(`/admin/companies/${c.id}/subscription`, { plan: e.target.value, status: c.subscription_status || "active" }), "Subscription updated")}
                              options={["founding", "pro", "enterprise"]} />
                          </div>
                          <div>
                            <label className={labelCls}>Subscription status</label>
                            <Select data-testid={`set-sub-status-${c.id}`} value={c.subscription_status || "active"}
                              onChange={(e) => act(() => api.put(`/admin/companies/${c.id}/subscription`, { plan: c.subscription_plan || "founding", status: e.target.value }), "Subscription updated")}
                              options={["active", "suspended", "expired"]} />
                          </div>
                          <Btn variant="ghost" data-testid={`toggle-adit-${c.id}`}
                            onClick={() => act(() => api.put(`/admin/companies/${c.id}/adit-trading`, { is_adit_trading: !c.is_adit_trading }), "Updated")}>
                            {c.is_adit_trading ? "Unmark ADIT Trading" : "Mark as ADIT Trading"}
                          </Btn>
                        </>
                      )}
                      {(c.documents || []).map((doc) => (
                        <a key={doc.file_id} href={`${API}/files/${doc.file_id}`} target="_blank" rel="noreferrer"
                          className="rounded-full border border-[#DFE6ED] px-3 py-1.5 text-xs font-semibold text-[#102A43]">
                          {doc.filename}
                        </a>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "submissions" && (
          <div className="mt-6 space-y-4" data-testid="admin-submissions">
            {d.submissions.length === 0 && (
              <Empty testId="admin-submissions-empty" title="No supplier submissions yet"
                sub="Suppliers submit part numbers and specifications from their own dashboard; they stay out of the public database until approved here." />
            )}

            {d.batches.filter((b) => b.pending > 0).length > 0 && (
              <Card data-testid="submission-batches">
                <h2 className="font-display text-base font-bold tracking-tight text-[#102A43]">
                  Uploads awaiting review
                </h2>
                <p className="mt-1 text-sm text-[#667788]">
                  Approve or reject an entire uploaded file in one click.
                </p>
                <div className="mt-4 space-y-3">
                  {pendingBatches.map((b) => (
                    <div key={b.batch_id} data-testid={`batch-row-${b.batch_id}`}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-[9px] border border-[#DFE6ED] p-3">
                      <div className="min-w-0">
                        <p className="break-all text-sm font-semibold text-[#102A43]">
                          {b.filename || "Spreadsheet upload"}
                        </p>
                        <p className="text-xs text-[#8598ac]">
                          {b.supplier_company_name} · {b.pending} pending of {b.total}
                          {b.approved ? ` · ${b.approved} approved` : ""}
                          {b.rejected ? ` · ${b.rejected} rejected` : ""}
                          {" · "}{(b.created_at || "").slice(0, 10)}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Btn variant="teal" data-testid={`approve-batch-${b.batch_id}`}
                          onClick={() => act(() => api.post("/admin/submissions/bulk-review", { action: "approve", batch_id: b.batch_id }), `Approved ${b.pending} part number(s)`)}
                          className="px-3.5 py-2 text-xs">
                          Approve all {b.pending}
                        </Btn>
                        <Btn variant="danger" data-testid={`reject-batch-${b.batch_id}`}
                          onClick={() => { setDraft({ batch_id: b.batch_id, action: "reject", count: b.pending }); setModal("bulk-review"); }}
                          className="px-3.5 py-2 text-xs">
                          Reject all
                        </Btn>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {d.submissions.some((s) => s.status !== "approved") && (
              <Card className="sticky top-20 z-20" data-testid="bulk-action-bar">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <label className="flex items-center gap-2 text-sm font-semibold text-[#334e68]">
                    <input type="checkbox" data-testid="select-all-submissions"
                      className="h-4 w-4 accent-[#147D78]"
                      checked={selected.length > 0 && selected.length === d.submissions.filter((s) => s.status !== "approved").length}
                      onChange={(e) => setSelected(e.target.checked
                        ? d.submissions.filter((s) => s.status !== "approved").map((s) => s.id) : [])} />
                    Select all reviewable ({d.submissions.filter((s) => s.status !== "approved").length})
                  </label>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-[#667788]" data-testid="selected-count">
                      {selected.length} selected
                    </span>
                    <Btn variant="teal" data-testid="bulk-approve-btn" disabled={!selected.length}
                      onClick={() => act(() => api.post("/admin/submissions/bulk-review", { action: "approve", submission_ids: selected }), `Approved ${selected.length} submission(s)`).then(() => setSelected([]))}
                      className="px-4 py-2.5 text-xs">
                      Approve selected
                    </Btn>
                    <Btn variant="ghost" data-testid="bulk-changes-btn" disabled={!selected.length}
                      onClick={() => { setDraft({ submission_ids: selected, action: "request_changes", count: selected.length }); setModal("bulk-review"); }}
                      className="px-4 py-2.5 text-xs">
                      Request changes
                    </Btn>
                    <Btn variant="danger" data-testid="bulk-reject-btn" disabled={!selected.length}
                      onClick={() => { setDraft({ submission_ids: selected, action: "reject", count: selected.length }); setModal("bulk-review"); }}
                      className="px-4 py-2.5 text-xs">
                      Reject selected
                    </Btn>
                  </div>
                </div>
              </Card>
            )}

            {d.submissions.map((sub) => (
              <Card key={sub.id} data-testid={`submission-card-${sub.id}`} className="min-w-0">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 gap-3">
                    {sub.status !== "approved" && (
                      <input type="checkbox" data-testid={`select-submission-${sub.id}`}
                        className="mt-1.5 h-4 w-4 shrink-0 accent-[#147D78]"
                        checked={selected.includes(sub.id)}
                        onChange={(e) => setSelected(e.target.checked
                          ? [...selected, sub.id] : selected.filter((x) => x !== sub.id))} />
                    )}
                    <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="break-all font-display text-lg font-black tracking-tight text-[#102A43]">
                        {sub.part_number}
                      </p>
                      <Pill tone={SUB_TONE[sub.status] || "neutral"} testId={`submission-status-${sub.id}`}>
                        {sub.status.replace(/_/g, " ")}
                      </Pill>
                      <Pill tone="neutral">{sub.source === "spreadsheet" ? "spreadsheet" : "form"}</Pill>
                    </div>
                    <p className="text-sm text-[#334e68]">{sub.name}</p>
                    <p className="mt-1 break-words text-xs text-[#8598ac]">
                      Submitted by {sub.supplier_company_name} · manufacturer {sub.manufacturer_name}
                      {sub.category ? ` · ${sub.category}` : ""}
                      {sub.list_price != null ? ` · ${money(sub.list_price, sub.currency)}` : ""}
                      {" · "}{(sub.created_at || "").slice(0, 10)}
                    </p>
                    </div>
                  </div>
                  {sub.status !== "approved" && (
                    <div className="flex flex-wrap gap-2">
                      <Btn variant="teal" data-testid={`approve-submission-${sub.id}`}
                        onClick={() => act(() => api.put(`/admin/submissions/${sub.id}/review`, { action: "approve" }), "Approved and published")}
                        className="px-4 py-2.5 text-xs">
                        Approve &amp; publish
                      </Btn>
                      <Btn variant="ghost" data-testid={`request-changes-${sub.id}`}
                        onClick={() => { setDraft({ id: sub.id, action: "request_changes", part_number: sub.part_number }); setModal("review"); }}
                        className="px-4 py-2.5 text-xs">
                        Request changes
                      </Btn>
                      <Btn variant="danger" data-testid={`reject-submission-${sub.id}`}
                        onClick={() => { setDraft({ id: sub.id, action: "reject", part_number: sub.part_number }); setModal("review"); }}
                        className="px-4 py-2.5 text-xs">
                        Reject
                      </Btn>
                    </div>
                  )}
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 border-t border-[#DFE6ED] pt-4 sm:grid-cols-4">
                  {specInputs.filter(([k]) => sub.spec?.[k] != null && sub.spec?.[k] !== "").map(([k, l]) => (
                    <div key={k} className="min-w-0">
                      <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">{l}</dt>
                      <dd className="break-words text-sm font-medium text-[#18232F]">{String(sub.spec[k])}</dd>
                    </div>
                  ))}
                </dl>
                {sub.technical_notes && (
                  <p className="mt-3 rounded-[9px] bg-[#F5F7FA] p-2.5 text-xs text-[#334e68]">{sub.technical_notes}</p>
                )}
                {sub.admin_notes && (
                  <p className="mt-3 rounded-[9px] bg-[#fff4df] p-2.5 text-xs text-[#8a6412]">
                    Reviewer notes: {sub.admin_notes}
                  </p>
                )}
                {(sub.documents || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {sub.documents.map((doc) => (
                      <a key={doc.file_id} href={`${API}/files/${doc.file_id}`} target="_blank" rel="noreferrer"
                        data-testid={`submission-doc-${doc.file_id}`}
                        className="rounded-full border border-[#DFE6ED] px-3 py-1.5 text-xs font-semibold text-[#102A43]">
                        {doc.filename}
                      </a>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {tab === "rfqs" && (
          <div className="mt-6 space-y-4" data-testid="admin-rfqs">
            {d.rfqs.length === 0 && <Empty testId="admin-rfqs-empty" title="No RFQs submitted yet" />}
            {d.rfqs.map((r) => (
              <Card key={r.id} data-testid={`rfq-row-${r.rfq_number}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-lg font-black tracking-tight text-[#102A43]">{r.rfq_number}</p>
                    <p className="text-sm text-[#334e68]">{r.title}</p>
                    <p className="mt-1 text-xs text-[#8598ac]">
                      {r.buyer_company_name} · {r.contact_name} · {r.email} {r.phone ? `· ${r.phone}` : ""} · source: {r.source}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Select data-testid={`rfq-status-${r.rfq_number}`} value={r.status}
                      onChange={(e) => act(() => api.put(`/admin/rfqs/${r.id}/status`, { status: e.target.value }), "Status updated")}
                      options={["open", "in_review", "closed", "awarded", "cancelled"]} />
                    <Btn variant="ghost" data-testid={`rematch-${r.rfq_number}`}
                      onClick={() => act(() => api.post(`/admin/rfqs/${r.id}/rematch`), "Suppliers re-matched")}>
                      Re-run matching
                    </Btn>
                  </div>
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 border-t border-[#DFE6ED] pt-4 sm:grid-cols-4">
                  {[["Quantity", `${r.quantity} ${r.unit}`], ["Location", r.delivery_location],
                    ["Matched suppliers", r.matched_count], ["Quotes", r.quote_count],
                    ["Delivery date", r.required_delivery_date || "—"], ["Incoterm", r.incoterm || "—"],
                    ["Certificates", (r.required_certificates || []).join(", ") || "—"],
                    ["Submitted", (r.created_at || "").slice(0, 10)]].map(([l, v]) => (
                    <div key={l}>
                      <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">{l}</dt>
                      <dd className="text-sm font-medium text-[#18232F]">{v}</dd>
                    </div>
                  ))}
                </dl>
                {r.attachments?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {r.attachments.map((a) => (
                      <a key={a.file_id} href={`${API}/files/${a.file_id}`} target="_blank" rel="noreferrer"
                        data-testid={`rfq-attachment-${a.file_id}`}
                        className="rounded-full border border-[#DFE6ED] px-3 py-1.5 text-xs font-semibold text-[#102A43]">
                        {a.filename}
                      </a>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {tab === "quotes" && (
          <div className="mt-6" data-testid="admin-quotes">
            <Card className="bg-[#fff4df]">
              <p className="text-sm text-[#8a6412]">
                Quotation prices are commercially confidential. Every access to this view is written to
                the audit log.
              </p>
            </Card>
            {d.quotes.length === 0 ? <Empty testId="admin-quotes-empty" title="No quotations submitted yet" /> : (
              <Table head={["RFQ", "Supplier", "Offered", "Unit", "Landed (est.)", "Availability", "Lead", "Compliance", "Status"]}>
                {d.quotes.map((q) => (
                  <tr key={q.id} data-testid={`admin-quote-row-${q.id}`}>
                    <td className="font-semibold text-[#102A43]">{q.rfq_number}</td>
                    <td>{q.supplier_company_name}</td>
                    <td>{q.offered_manufacturer} {q.part_number}</td>
                    <td>{money(q.unit_price, q.currency)}</td>
                    <td>{money(q.landed_cost?.total_landed_cost, q.currency)}</td>
                    <td><AvailabilityChip quote={q} /></td>
                    <td>{q.lead_time_days != null ? `${q.lead_time_days} d` : "—"}</td>
                    <td><StatusChip status={q.technical_compliance === "meets" ? "meets" : "deviation"} compact /></td>
                    <td><Pill tone={STATUS_TONE[q.status] || "neutral"}>{q.status}</Pill></td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
        )}

        {tab === "awards" && (
          <div className="mt-6 space-y-4" data-testid="admin-awards">
            {d.awards.length === 0 && <Empty testId="admin-awards-empty" title="No awards yet" />}
            {d.awards.map((a) => (
              <Card key={a.id} data-testid={`admin-award-row-${a.rfq_number}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-lg font-black tracking-tight text-[#102A43]">{a.rfq_number}</p>
                    <p className="text-sm text-[#334e68]">
                      {a.buyer_company_name} → {a.supplier_company_name} · {a.part_number}
                    </p>
                    <p className="mt-1 text-xs text-[#8598ac]">
                      {a.route === "managed" ? "Managed by ADIT (requires review & acceptance)" : "Direct purchase"} ·
                      estimated landed cost {money(a.total_landed_cost, a.currency)}
                    </p>
                    {a.notes && <p className="mt-2 rounded-[9px] bg-[#F5F7FA] p-2.5 text-xs text-[#334e68]">{a.notes}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Pill tone={STATUS_TONE[a.status] || "neutral"} testId={`award-status-${a.rfq_number}`}>
                      {a.status.replace(/_/g, " ")}
                    </Pill>
                    <Select data-testid={`set-award-status-${a.rfq_number}`} value={a.status}
                      onChange={(e) => act(() => api.put(`/admin/awards/${a.id}/status`, { status: e.target.value }), "Award updated")}
                      options={["managed_review", "managed_accepted", "managed_declined", "awarded_direct", "in_progress", "completed", "cancelled"]} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {tab === "reports" && (
          <div className="mt-6" data-testid="admin-reports">
            <AdminAnalytics />
          </div>
        )}

        {tab === "spec-fields" && (
          <div className="mt-6" data-testid="admin-spec-fields-tab">
            <AdminSpecCategories />
          </div>
        )}

        {tab === "support" && (
          <div className="mt-6" data-testid="admin-support-tab">
            <AdminSupport />
          </div>
        )}

        {tab === "payments" && (
          <div className="mt-6" data-testid="admin-payments-tab">
            <AdminPayments />
          </div>
        )}

        {tab === "matching" && d.rules && (
          <div className="mt-6" data-testid="admin-matching">
            <Card>
              <h2 className="font-display text-lg font-bold tracking-tight text-[#102A43]">
                Supplier matching rules
              </h2>
              <p className="mt-1 text-sm text-[#667788]">
                An RFQ is only issued to a supplier whose score reaches the minimum. Exact-brand
                enquiries are never sent to suppliers who do not represent that brand.
              </p>
              <div className="mt-5 grid gap-4 sm:grid-cols-3">
                {[["category_weight", "Category weight"], ["subcategory_weight", "Subcategory weight"],
                  ["brand_weight", "Brand weight"], ["market_weight", "Market weight"],
                  ["min_score", "Minimum score to notify"], ["unverified_penalty", "Unverified penalty"]].map(([k, l]) => (
                  <Field key={k} label={l}>
                    <Input data-testid={`rule-${k}`} type="number" value={d.rules[k]}
                      onChange={(e) => setD({ ...d, rules: { ...d.rules, [k]: Number(e.target.value) } })} />
                  </Field>
                ))}
                <Field label="Require verified suppliers">
                  <Select data-testid="rule-require_verified" value={String(d.rules.require_verified)}
                    onChange={(e) => setD({ ...d, rules: { ...d.rules, require_verified: e.target.value === "true" } })}
                    options={[["true", "Yes — penalise unverified"], ["false", "No"]]} />
                </Field>
              </div>
              <Btn data-testid="save-rules-btn" className="mt-5"
                onClick={() => act(() => api.put("/admin/matching-rules", d.rules), "Matching rules saved")}>
                Save matching rules
              </Btn>
            </Card>
          </div>
        )}

        {tab === "audit" && (
          <div className="mt-6" data-testid="admin-audit">
            <Table head={["When", "Actor", "Role", "Action", "Entity", "Sensitive", "IP"]}>
              {d.logs.map((l) => (
                <tr key={l.id} data-testid={`audit-row-${l.id}`}>
                  <td className="text-xs">{(l.created_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="text-xs">{l.actor_email || "anonymous"}</td>
                  <td className="text-xs">{l.actor_role}</td>
                  <td className="text-xs font-semibold text-[#102A43]">{l.action}</td>
                  <td className="text-xs">{l.entity}{l.entity_id ? ` · ${l.entity_id.slice(-6)}` : ""}</td>
                  <td>{l.sensitive ? <Pill tone="warn">sensitive</Pill> : ""}</td>
                  <td className="text-xs">{l.ip || "—"}</td>
                </tr>
              ))}
            </Table>
          </div>
        )}
      </div>

      {modal === "bulk-review" && (
        <Modal title={draft.action === "reject" ? "Reject submissions" : "Request changes"} onClose={() => setModal(null)}>
          <p className="text-sm text-[#334e68]">
            This will {draft.action === "reject" ? "reject" : "return"}{" "}
            <strong>{draft.count}</strong> submission(s)
            {draft.batch_id ? " from this spreadsheet upload" : ""}. Each affected supplier receives
            one summary email with your notes.
          </p>
          <Field label="Reviewer notes" className="mt-4">
            <Textarea rows={4} data-testid="bulk-review-notes" value={draft.admin_notes || ""}
              onChange={(e) => setDraft({ ...draft, admin_notes: e.target.value })}
              placeholder="e.g. certificate references missing across the file" />
          </Field>
          <Btn data-testid="confirm-bulk-review-btn" className="mt-5 w-full"
            onClick={() => act(() => api.post("/admin/submissions/bulk-review", {
              action: draft.action, admin_notes: draft.admin_notes || null,
              submission_ids: draft.submission_ids || [], batch_id: draft.batch_id || null,
            }), draft.action === "reject" ? "Submissions rejected" : "Changes requested")
              .then(() => setSelected([]))}>
            {draft.action === "reject" ? `Reject ${draft.count}` : `Request changes on ${draft.count}`}
          </Btn>
        </Modal>
      )}

      {modal === "review" && (
        <Modal title={draft.action === "reject" ? "Reject submission" : "Request changes"} onClose={() => setModal(null)}>
          <p className="text-sm text-[#334e68]">
            {draft.part_number} — the supplier receives your notes by email.
            {draft.action === "request_changes"
              ? " They can edit and resubmit, which returns it to this queue."
              : ""}
          </p>
          <Field label="Reviewer notes" className="mt-4">
            <Textarea rows={4} data-testid="review-notes" value={draft.admin_notes || ""}
              onChange={(e) => setDraft({ ...draft, admin_notes: e.target.value })}
              placeholder="e.g. IECEx certificate reference missing; cable range does not match the datasheet" />
          </Field>
          <Btn data-testid="confirm-review-btn" className="mt-5 w-full"
            onClick={() => act(() => api.put(`/admin/submissions/${draft.id}/review`,
              { action: draft.action, admin_notes: draft.admin_notes || null }),
              draft.action === "reject" ? "Submission rejected" : "Changes requested")}>
            {draft.action === "reject" ? "Reject submission" : "Send change request"}
          </Btn>
        </Modal>
      )}

      {modal === "mfr" && (
        <Modal title={draft.id ? "Edit manufacturer" : "Add manufacturer"} onClose={() => setModal(null)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name">
              <Input data-testid="mfr-input-name" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </Field>
            <Field label="Country">
              <CountrySelect testId="mfr-input-country" value={draft.country || ""}
                onChange={(v) => setDraft({ ...draft, country: v })} />
            </Field>
            {[["website", "Website"], ["notes", "Notes"]].map(([k, l]) => (
              <Field key={k} label={l} className={k === "notes" ? "sm:col-span-2" : ""}>
                <Input data-testid={`mfr-input-${k}`} value={draft[k] || ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />
              </Field>
            ))}
          </div>
          <Btn data-testid="save-mfr-btn" onClick={saveMfr} className="mt-6 w-full">Save manufacturer</Btn>
        </Modal>
      )}

      {modal === "product" && (
        <Modal wide title={draft.id ? "Edit product" : "Add product"} onClose={() => setModal(null)}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Part number *"><Input data-testid="product-input-part_number" value={draft.part_number || ""} onChange={(e) => setDraft({ ...draft, part_number: e.target.value })} /></Field>
            <Field label="Product name *"><Input data-testid="product-input-name" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></Field>
            <Field label="Manufacturer *">
              <Select data-testid="product-input-manufacturer" value={draft.manufacturer_id || ""}
                onChange={(e) => setDraft({ ...draft, manufacturer_id: e.target.value })}
                options={d.manufacturers.map((m) => [m.id, m.name])} />
            </Field>
            <Field label="Brand"><Input data-testid="product-input-brand" value={draft.brand || ""} onChange={(e) => setDraft({ ...draft, brand: e.target.value })} /></Field>
            <Field label="Product family"><Input data-testid="product-input-family" value={draft.product_family || ""} onChange={(e) => setDraft({ ...draft, product_family: e.target.value })} /></Field>
            <Field label="Category"><Input data-testid="product-input-category" value={draft.category || ""} onChange={(e) => setDraft({ ...draft, category: e.target.value })} /></Field>
            <Field label="Subcategory"><Input data-testid="product-input-subcategory" value={draft.subcategory || ""} onChange={(e) => setDraft({ ...draft, subcategory: e.target.value })} /></Field>
            <Field label="Country of manufacture"><CountrySelect testId="product-input-country" value={draft.country_of_manufacture || ""} onChange={(v) => setDraft({ ...draft, country_of_manufacture: v })} /></Field>
            <Field label="Product image URL"><Input data-testid="product-input-image" value={draft.image_url || ""} onChange={(e) => setDraft({ ...draft, image_url: e.target.value })} /></Field>
            <Field label="List price"><Input data-testid="product-input-price" type="number" step="0.01" value={draft.list_price ?? ""} onChange={(e) => setDraft({ ...draft, list_price: e.target.value })} /></Field>
            <Field label="Currency"><Select data-testid="product-input-currency" value={draft.currency || "AED"} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} options={["AED", "USD", "EUR", "GBP"]} /></Field>
            <Field label="Technical notes" className="sm:col-span-2">
              <Textarea rows={2} data-testid="product-input-notes" value={draft.technical_notes || ""} onChange={(e) => setDraft({ ...draft, technical_notes: e.target.value })} />
            </Field>
          </div>
          <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#147D78]">
            Technical specification
          </p>
          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            {specInputs.map(([k, l, type, options]) => (
              <Field key={k} label={l}>
                {options && options.length ? (
                  <Select data-testid={`spec-input-${k}`} value={draft.spec?.[k] ?? ""}
                    options={[["", "Not stated"], ...options.map((o) => [o, o])]}
                    onChange={(e) => setDraft({ ...draft, spec: { ...draft.spec, [k]: e.target.value } })} />
                ) : (
                  <Input data-testid={`spec-input-${k}`} type={type} step="0.1" value={draft.spec?.[k] ?? ""}
                    onChange={(e) => setDraft({ ...draft, spec: { ...draft.spec, [k]: e.target.value } })} />
                )}
              </Field>
            ))}
          </div>
          <Btn data-testid="save-product-btn" onClick={saveProduct} className="mt-6 w-full">Save product</Btn>
        </Modal>
      )}

      {modal === "equiv" && (
        <Modal title="Link equivalent product" onClose={() => setModal(null)}>
          <div className="grid gap-4">
            {[["product_id", "Product"], ["equivalent_product_id", "Equivalent product"]].map(([k, l]) => (
              <Field key={k} label={l}>
                <Select data-testid={`equiv-input-${k}`} value={draft[k] || ""}
                  onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                  options={d.products.map((p) => [p.id, `${p.manufacturer_name} — ${p.part_number}`])} />
              </Field>
            ))}
            <Field label="Notes">
              <Input data-testid="equiv-input-notes" value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
            </Field>
          </div>
          <Btn data-testid="save-equiv-btn" className="mt-6 w-full"
            onClick={() => act(() => api.post("/admin/equivalents", draft), "Equivalent linked")}>
            Link products
          </Btn>
        </Modal>
      )}

      {modal === "bulk-product" && (
        <Modal title={`Bulk edit ${draft.count} product(s)`} onClose={() => setModal(null)} wide>
          <p className="text-sm text-[#667788]">
            Only the fields you fill in are applied. Anything left blank stays as it is on each product.
          </p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Manufacturer">
              <Select data-testid="bulk-manufacturer" value={draft.manufacturer_id || ""}
                onChange={(e) => setDraft({ ...draft, manufacturer_id: e.target.value })}
                options={[["", "Leave unchanged"], ...d.manufacturers.map((m) => [m.id, m.name])]} />
            </Field>
            <Field label="Category">
              <Select data-testid="bulk-category" value={draft.category || ""}
                onChange={(e) => setDraft({ ...draft, category: e.target.value, spec_key: "", spec_value: "" })}
                options={[["", "Leave unchanged"], ...schema.published.map((c) => [c, c])]} />
            </Field>
            <Field label="Subcategory">
              <Input data-testid="bulk-subcategory" value={draft.subcategory || ""}
                onChange={(e) => setDraft({ ...draft, subcategory: e.target.value })} />
            </Field>
            <Field label="Brand">
              <Input data-testid="bulk-brand" value={draft.brand || ""}
                onChange={(e) => setDraft({ ...draft, brand: e.target.value })} />
            </Field>
            <Field label="Country of manufacture">
              <CountrySelect testId="bulk-country" value={draft.country_of_manufacture || ""}
                onChange={(v) => setDraft({ ...draft, country_of_manufacture: v })} />
            </Field>
            <Field label="List price">
              <Input data-testid="bulk-price" type="number" step="0.01" value={draft.list_price ?? ""}
                onChange={(e) => setDraft({ ...draft, list_price: e.target.value })} />
            </Field>
            <Field label="Currency">
              <Input data-testid="bulk-currency" value={draft.currency || ""} placeholder="AED"
                onChange={(e) => setDraft({ ...draft, currency: e.target.value })} />
            </Field>
          </div>
          <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#147D78]">
            One specification field
          </p>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <Field label="Field" hint={draft.category ? undefined : "Pick a category above to list its fields"}>
              <Select data-testid="bulk-spec-key" value={draft.spec_key || ""}
                onChange={(e) => setDraft({ ...draft, spec_key: e.target.value, spec_value: "" })}
                options={[["", "None"], ...schema.inputsFor(draft.category).map(([k, l]) => [k, l])]} />
            </Field>
            <Field label="Value">
              {(() => {
                const f = schema.inputsFor(draft.category).find(([k]) => k === draft.spec_key);
                const options = f?.[3];
                return options && options.length ? (
                  <Select data-testid="bulk-spec-value" value={draft.spec_value || ""}
                    options={[["", "Leave unchanged"], ...options.map((o) => [o, o])]}
                    onChange={(e) => setDraft({ ...draft, spec_value: e.target.value })} />
                ) : (
                  <Input data-testid="bulk-spec-value" type={f?.[2] || "text"} step="0.1"
                    value={draft.spec_value || ""} disabled={!draft.spec_key}
                    onChange={(e) => setDraft({ ...draft, spec_value: e.target.value })} />
                );
              })()}
            </Field>
          </div>
          <Btn data-testid="save-bulk-product-btn" className="mt-6 w-full" onClick={bulkEditProducts}>
            Apply to {draft.count} product(s)
          </Btn>
        </Modal>
      )}

      {modal === "bulk-equiv" && (
        <Modal title={`Set notes on ${draft.count} link(s)`} onClose={() => setModal(null)}>
          <Field label="Notes">
            <Input data-testid="bulk-equiv-notes" value={draft.notes || ""}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              placeholder="e.g. Verified equivalent — same certificate" />
          </Field>
          <Btn data-testid="save-bulk-equiv-btn" className="mt-6 w-full"
            onClick={() => act(async () => {
              await api.post("/admin/equivalents/bulk", { equivalent_ids: draft.equivalent_ids, notes: draft.notes });
              setPickedEquivs([]);
            }, "Notes updated")}>
            Apply notes
          </Btn>
        </Modal>
      )}


      {modal === "category" && (
        <Modal title="Category" onClose={() => setModal(null)}>
          <Field label="Category name">
            <Input data-testid="category-input-name" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </Field>
          <Field label="Subcategories" className="mt-4" hint="Comma separated">
            <Input data-testid="category-input-subs" value={draft.subcategories || ""} onChange={(e) => setDraft({ ...draft, subcategories: e.target.value })} />
          </Field>
          <Btn data-testid="save-category-btn" className="mt-6 w-full"
            onClick={() => act(() => api.post("/admin/categories", {
              name: draft.name,
              subcategories: (draft.subcategories || "").split(",").map((x) => x.trim()).filter(Boolean),
            }), "Category saved")}>
            Save category
          </Btn>
        </Modal>
      )}

      {modal === "import" && (
        <Modal wide title="Import products" onClose={() => setModal(null)}>
          <div className="flex gap-2">
            {[["file", "Spreadsheet (CSV / Excel)"], ["json", "JSON rows"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`import-mode-${v}`}
                onClick={() => setDraft({ ...draft, mode: v, result: null })}
                className={`rounded-[9px] px-3.5 py-2 text-sm font-bold ${
                  (draft.mode || "file") === v ? "bg-[#102A43] text-white" : "border border-[#DFE6ED] bg-white text-[#102A43]"}`}>
                {l}
              </button>
            ))}
          </div>

          {(draft.mode || "file") === "file" ? (
            <div className="mt-5">
              <p className="text-sm text-[#667788]">
                Upload one row per part number. Required columns: <strong>manufacturer</strong> and{" "}
                <strong>part_number</strong>. Optional: name, brand, product_family, category,
                subcategory, country_of_manufacture, technical_notes, list_price, currency,
                thread_type, thread_size, cable_type, armour_type, cable_diameter_min,
                cable_diameter_max, material, ex_protection, gas_group, epl, ip_rating, temp_min,
                temp_max, certificate_reference, atex_certificate. Common header variants such as
                “Part No”, “IECEx”, “IP”, “Price” and “Origin” are mapped automatically. Existing
                part numbers for the same manufacturer are updated.
              </p>
              <a href={`${API}/../sample-import.csv`} className="hidden">sample</a>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <input ref={importFileRef} data-testid="import-file-input" type="file" accept=".csv,.xlsx,.xlsm,.txt"
                  className="hidden" onChange={(e) => setDraft({ ...draft, file: e.target.files?.[0] || null, result: null })} />
                <Btn variant="ghost" data-testid="choose-import-file-btn" onClick={() => importFileRef.current?.click()}>
                  Choose file
                </Btn>
                <span className="text-sm text-[#334e68]" data-testid="import-file-name">
                  {draft.file ? draft.file.name : "No file selected"}
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Btn variant="ghost" data-testid="preview-import-btn" loading={importing === "dry"}
                  disabled={!draft.file} onClick={() => runFileImport(true)}>
                  Check file
                </Btn>
                <Btn data-testid="run-file-import-btn" loading={importing === "run"}
                  disabled={!draft.file} onClick={() => runFileImport(false)}>
                  Import products
                </Btn>
              </div>
              {draft.result && (
                <div className="mt-5 rounded-[9px] border border-[#DFE6ED] bg-[#F5F7FA] p-4 text-sm" data-testid="import-result">
                  {draft.result.dry_run ? (
                    <>
                      <p className="font-semibold text-[#102A43]">
                        {draft.result.rows_detected} data row(s) detected
                      </p>
                      <p className="mt-1 text-xs text-[#667788]">
                        Columns recognised: {(draft.result.columns || []).join(", ") || "none"}
                      </p>
                      {draft.result.rows_missing_required_fields?.length > 0 && (
                        <p className="mt-2 text-xs font-semibold text-[#B42318]">
                          Rows missing manufacturer or part_number: {draft.result.rows_missing_required_fields.join(", ")}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="font-semibold text-[#102A43]">
                        {draft.result.created} created · {draft.result.updated} updated ·{" "}
                        {draft.result.errors?.length || 0} failed
                      </p>
                      {draft.result.errors?.length > 0 && (
                        <ul className="mt-2 space-y-1 text-xs text-[#B42318]">
                          {draft.result.errors.slice(0, 8).map((e) => (
                            <li key={e.row}>Row {e.row}: {e.error}</li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-5">
              <p className="text-sm text-[#667788]">
                Paste a JSON array. Each row needs <code>manufacturer</code> and <code>part_number</code>.
              </p>
              <Textarea data-testid="import-json" rows={10} className="mt-4 font-mono text-xs"
                value={draft.json || ""} onChange={(e) => setDraft({ ...draft, json: e.target.value })}
                placeholder='[{"manufacturer":"CMP Products","part_number":"32 A2F","name":"A2F M32","category":"Cable Glands - Ex d / Ex e (SWA Armoured)","thread_size":"M32","gas_group":"IIC","ip_rating":"IP66","list_price":68.5}]' />
              <Btn data-testid="run-import-btn" onClick={importProducts} className="mt-5 w-full">Run import</Btn>
            </div>
          )}
        </Modal>
      )}
    </DashboardShell>
  );
}
